/* eslint-disable no-unused-vars */
import React from "react";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import Teachr from "../../../Objects/models/Teachr";
import { MAX_COURSES_PER_MONTH } from "../../../Utils/monthlyQuota";
import { TdpDescEnum } from "../../../Enum/TdpEnum";
import { DV2 } from "../theme";
import { euros, hoursFromHalfHours, monthName, studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { declaredAmountHelp, studentMonthFigures } from "../layout/StudentMonthPanel";
import { Card, InfoTip, fieldStyle, useDv2Theme } from "../layout/Ui";
import { isStudentSelectable } from "./useStudentSelection";
import { isRelationTeachrChild } from "./relationTeachr";

interface Props {
    teachr: Teachr;
    clients: any[];
    total: number;
    page: number;
    pageSize: number;
    search: string;
    loading: boolean;
    checkingId: number | null;
    selectedChildId: number | null;
    onSearch: (value: string) => void;
    onPage: (page: number) => void;
    onSelect: (child: any) => void;
    /** Rendu sous la ligne sélectionnée (encart du mois en version mobile). */
    renderSelectedExtra?: (child: any) => React.ReactNode;
}

const lastMonth = () => monthName(moment().subtract(1, "month"));

/**
 * Tableau des élèves de l'étape 1 (maquettes 1a et 2a) : recherche, colonnes
 * heures déclarées, cours du mois et montant, pagination. En version mobile,
 * chaque ligne devient une carte avec les deux informations utiles.
 */
const StudentTable = ({ teachr, clients, total, page, pageSize, search, loading, checkingId, selectedChildId, onSearch, onPage, onSelect, renderSelectedExtra }: Props) => {
    const { primary, fontFamily, text, brandName } = useDv2Theme();
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const from = total === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(total, (page + 1) * pageSize);

    const quotaColor = (remaining: number) => (remaining === 0 ? DV2.danger : remaining <= 2 ? DV2.warning : DV2.text);

    const pages = (): Array<number | "…"> => {
        if (pageCount <= 6) return Array.from({ length: pageCount }, (_, i) => i);
        const set = new Set<number>([0, 1, 2, page - 1, page, page + 1, pageCount - 1].filter((p) => p >= 0 && p < pageCount));
        const sorted = Array.from(set).sort((a, b) => a - b);
        const result: Array<number | "…"> = [];
        sorted.forEach((p, i) => {
            if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…");
            result.push(p);
        });
        return result;
    };

    const gridCols = "36px minmax(0,1.5fr) 1fr 1fr 1.2fr";

    return (
        <Card padding={0} style={{ overflow: "hidden", fontFamily }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${DV2.borderSoft}`, display: "flex", alignItems: "center", gap: 12 }}>
                <input
                    type="search"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Filtrer par nom ou par e-mail"
                    aria-label={`Filtrer les ${vocab.clients()} par nom ou par e-mail`}
                    style={{ ...fieldStyle(), background: DV2.pageBg, fontWeight: 500, fontSize: 14 }}
                />
                <span style={{ fontSize: 13, color: text, whiteSpace: "nowrap" }}>{total} {vocab.clients()}</span>
            </div>

            {/* En-tête (web) */}
            <div className="hidden md:grid" style={{ gridTemplateColumns: gridCols, padding: "10px 20px", fontSize: 12, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: `1px solid ${DV2.borderSoft}`, gap: 12 }}>
                <span />
                <span>{vocab.client()}</span>
                <span>Heures déclarées</span>
                <span>{vocab.courses()} ce mois</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Montant déclaré <InfoTip text={declaredAmountHelp()} label="Ce que compte le montant déclaré" /></span>
            </div>

            {loading && clients.length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: text, fontSize: 14 }}>
                    <CircularProgress size={18} style={{ marginRight: 8, verticalAlign: "middle" }} /> Chargement de vos {vocab.clients()}…
                </div>
            )}
            {!loading && clients.length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: text, fontSize: 14 }}>
                    Aucun {vocab.client()} trouvé{search ? ` pour « ${search} »` : ""}.
                </div>
            )}

            <div role="radiogroup" aria-label={`Choix ${vocab.theClient()}`}>
                {clients.map((child) => {
                    const selectable = isStudentSelectable(child);
                    const selected = selectedChildId === child.childId;
                    const checking = checkingId === child.childId;
                    const f = studentMonthFigures(child, teachr);
                    const active = child?.status ? child.status === "APPAREILLAGE_VALIDE" : true;
                    const name = studentFullName(child);
                    // relation Teach'r : le montant déclaré comprend la part de la plateforme, non montrée au prof
                    const hideAmount = isRelationTeachrChild(child);
                    return (
                        <div key={child.childId} style={{ borderBottom: `1px solid ${DV2.borderSoft}`, background: selected ? DV2.tint : "transparent" }}>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                disabled={!selectable || checkingId !== null}
                                title={!selectable ? TdpDescEnum.STATUS_6_ACTIVE_DEFAULT : undefined}
                                onClick={() => onSelect(child)}
                                className="w-full text-left md:grid"
                                style={{ gridTemplateColumns: gridCols, gap: 12, padding: "14px 20px", alignItems: "center", background: "transparent", border: "none", cursor: selectable ? "pointer" : "not-allowed", opacity: selectable ? 1 : 0.45, fontFamily, color: DV2.text, display: "grid" }}
                            >
                                <div aria-hidden style={{ width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 13, background: selected ? primary : "transparent", color: "#fff", border: selected ? "none" : "2px solid #C9D3E0" }}>
                                    {checking ? <CircularProgress size={12} color="inherit" style={{ color: primary }} /> : selected ? "✓" : ""}
                                </div>
                                <div className="col-span-1" style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 800, fontSize: 14 }}>{name}</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: active ? DV2.success : DV2.placeholder }}>
                                        Avance immédiate {active ? "active" : "non activée"}
                                    </div>
                                </div>
                                <div className="hidden md:block">
                                    <div style={{ fontWeight: 800, fontSize: 14 }}>{hoursFromHalfHours(child?.duration)}</div>
                                    <div style={{ fontSize: 12, color: text }}>{hoursFromHalfHours(f.lastMonthHalfHours)} en {lastMonth()}</div>
                                </div>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 14, color: quotaColor(f.remainingCourses) }}>{f.remainingCourses} restant{f.remainingCourses > 1 ? "s" : ""} sur {MAX_COURSES_PER_MONTH}</div>
                                    <div style={{ fontSize: 12, color: text }}>
                                        {f.lastMonthCourses === null ? `${f.quota && f.quota.others > 0 ? "dont " : ""}${f.mineCourses} par vous` : `${f.lastMonthCourses} en ${lastMonth()}`}
                                    </div>
                                </div>
                                {hideAmount ? (
                                    <div className="hidden md:block" style={{ fontSize: 12, color: text, lineHeight: 1.4 }}>Tarif convenu avec {brandName}</div>
                                ) : (
                                    <div className="hidden md:block">
                                        <div style={{ fontWeight: 800, fontSize: 14 }}>{euros(f.declaredAmount)}</div>
                                        <div style={{ fontSize: 12, color: text }}>{euros(f.lastMonthAmount)} en {lastMonth()}</div>
                                    </div>
                                )}
                                {!hideAmount && (
                                    <div className="md:hidden" style={{ gridColumn: "2 / -1", fontSize: 12, color: text, display: "flex", alignItems: "center", gap: 6 }}>{euros(f.declaredAmount)} déclarés <InfoTip text={declaredAmountHelp()} label="Ce que compte le montant déclaré" /></div>
                                )}
                            </button>
                            {selected && renderSelectedExtra && <div style={{ padding: "0 20px 14px" }}>{renderSelectedExtra(child)}</div>}
                        </div>
                    );
                })}
            </div>

            {total > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", fontSize: 13, color: text, flexWrap: "wrap", gap: 8 }}>
                    <span>{from} à {to} sur {total}</span>
                    <nav aria-label="Pagination" style={{ display: "flex", gap: 6 }}>
                        {pages().map((p, i) => p === "…" ? (
                            <span key={`gap-${i}`} style={{ padding: "6px 10px" }}>…</span>
                        ) : (
                            <button
                                key={p}
                                type="button"
                                onClick={() => onPage(p)}
                                aria-current={p === page ? "page" : undefined}
                                style={{ padding: "6px 10px", borderRadius: 8, fontFamily, fontWeight: 800, cursor: "pointer", background: p === page ? primary : "#fff", color: p === page ? "#fff" : DV2.text, border: p === page ? "none" : `1px solid ${DV2.border}` }}
                            >
                                {p + 1}
                            </button>
                        ))}
                    </nav>
                </div>
            )}
        </Card>
    );
};

export default StudentTable;
